import { ReactElement, useRef, useState } from 'react';
import classNames from 'classnames';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { SourceType } from '@sc/graphql';
import { SourceIcon, sourceNiceName } from '../util/sources';
import { useTheme } from '../providers/ThemeContext';

export type CitationChunkType = {
  title: string;
  description: string | null;
  favicon: string | null;
  source: { type: SourceType };
};

interface Props {
  number: number;
  chunk: CitationChunkType;
  /** Opens the sources drawer; undefined renders a non-interactive chip. */
  onOpenSources?: (() => void) | undefined;
}

export const CitationChip = ({
  number,
  chunk,
  onOpenSources,
}: Props): ReactElement => {
  const { inverted } = useTheme();

  const [isHovered, setIsHovered] = useState(false);

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);

    setIsHovered(true);
  };

  // Delayed so the pointer can cross the gap into the portalled panel.
  const scheduleHide = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);

    closeTimer.current = setTimeout(() => setIsHovered(false), 120);
  };

  return (
    <Popover as="sup" className="mx-1 inline-block mt-1">
      <PopoverButton
        onClick={onOpenSources}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
        className={classNames(
          'inline-flex items-center justify-center rounded-full size-6 max-w-40 text-xs leading-none font-medium border transition-opacity hover:opacity-75',
          onOpenSources ? 'cursor-pointer' : 'cursor-default',
          inverted
            ? 'border-black-t-700 bg-black-t-900 text-black-t-200'
            : 'text-text-low-priority border-grey-t-700 bg-background-secondary text-white'
        )}
      >
        <span className="truncate">{number}</span>
      </PopoverButton>
      {isHovered && (
        <PopoverPanel
          static
          anchor={{ to: 'bottom start', gap: 4, padding: 8 }}
          onClick={onOpenSources}
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
          className={classNames(
            'z-200 w-72 text-left font-normal rounded-xl p-4 space-y-2 shadow-lg',
            onOpenSources && 'cursor-pointer',
            inverted
              ? 'bg-white border border-black-t-800 text-black'
              : 'bg-background-secondary text-white'
          )}
        >
          <div
            className={classNames(
              'flex items-center space-x-2',
              inverted ? 'text-grey-600' : 'text-text-low-priority'
            )}
          >
            {chunk.favicon ? (
              <img
                src={chunk.favicon}
                alt=""
                className="size-4 shrink-0 rounded-xs"
              />
            ) : (
              <SourceIcon
                className="size-4 shrink-0"
                type={chunk.source.type}
              />
            )}
            <span className="text-sm truncate">
              {sourceNiceName[chunk.source.type]}
            </span>
          </div>
          <p className="text-base font-semibold">{chunk.title}</p>
          {chunk.description && (
            <p
              className={classNames(
                'text-sm line-clamp-3',
                inverted ? 'text-grey-600' : 'text-text-low-priority'
              )}
            >
              {chunk.description}
            </p>
          )}
        </PopoverPanel>
      )}
    </Popover>
  );
};
